import { useTranslation } from "@equiem/web-ng-lib";
import { Tag } from "@equiem/react-end-user-ui";
import React from "react";
import { ProductDealListingFragment } from "../../../generated/gateway-client";
import { DealTag } from "./DealTag";
import { getAvailabilityBadge } from "./useAvailabilityStatus";
import { ProductListing } from "./ProductListing";
import { FaStore } from "react-icons/fa";
import { BsCalendar, BsClock } from "react-icons/bs";
import { formatEndDate, formatDateRange, parseLocalDate } from "./dealAvailabilityUtils";

interface Props {
  product: ProductDealListingFragment;
  className?: string;
  metric?: { ns: string; nx: number };
  showVendor?: boolean;
  isClickable?: boolean;
}

export const ListingDeal: React.FC<Props> = ({ metric, product, className = "", showVendor = true, isClickable = true }) => {
  const { t, i18n } = useTranslation();

  const metricExt = metric == null ? "" : `?ns=${metric.ns}&nx=${metric.nx}`;
  const { dateRange, tradingDaysLabel } = product.availability;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDateStr = dateRange != null ? formatEndDate(dateRange.endDate, i18n.language) : null;
  const dateRangeStr = dateRange != null && today < parseLocalDate(dateRange.startDate) ? formatDateRange(dateRange.startDate, dateRange.endDate, i18n.language) : null;
  const showEndsDate = endDateStr != null && dateRange != null && today >= parseLocalDate(dateRange.startDate) && today < parseLocalDate(dateRange.endDate);
  const showLimitedFooter = showEndsDate || tradingDaysLabel != null;

  const badge = getAvailabilityBadge(product.availability.tagText);

  return (
    <>
    <ProductListing
      className={className}
      name={product.name}
      image={product.image}
      available={product.availability.isAvailable}
      isViewable={true}
      disableLink={!isClickable}
      suffix={
        <>
          {product.showOverlay ? <DealTag top="1px" left="1px" /> : undefined}
          {badge != null ? (
            <div className="availability-tag">
              <Tag variant={badge.variant} size="large" emphasis="high" border>{badge.label}</Tag>
            </div>
          ) : null}
        </>
      }
      description={product.description}
      url={{
        as: `/store/deal/${product.uuid}${metricExt}`,
        href: "/store/deal/[uuid]",
      }}
    >
      <div className="mt-auto">
        {showVendor ? (
          <div className="vendor-name pb-2 d-flex align-items-center">
            <FaStore /> <span className="ml-2">{product.vendor.name}</span>
          </div>
        ) : null}
        <div
          className="availability-footer-content"
          style={{ overflow: "hidden", minHeight: "var(--deal-availability-height, 0px)" }}
        >
          {dateRangeStr != null ? (
            <>
              <hr className="my-2" />
              <div
                className="d-flex align-items-start flex-wrap small"
                style={{ gap: "0.25rem 0.75rem", color: "#000" }}
              >
                <div className="d-flex align-items-center" style={{ gap: "0.25rem", whiteSpace: "nowrap" }}>
                  <BsCalendar className="flex-shrink-0" />
                  <span>{dateRangeStr}</span>
                </div>
              </div>
            </>
          ) : showLimitedFooter ? (
            <>
              <hr className="my-2" />
              <div
                className="d-flex align-items-start flex-wrap small"
                style={{ gap: "0.25rem 0.75rem", color: "#000" }}
              >
                {showEndsDate ? (
                  <div className="d-flex align-items-center" style={{ gap: "0.25rem", whiteSpace: "nowrap" }}>
                    <BsCalendar className="flex-shrink-0" />
                    <span>{t("main.dealEndsDate", { date: endDateStr })}</span>
                  </div>
                ) : null}
                {showEndsDate && tradingDaysLabel != null ? (
                 <span style={{ color: "#666666", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "1rem", lineHeight: 1 }}>·</span>
                ) : null}
                {tradingDaysLabel != null ? (
                  <div className="d-flex align-items-center" style={{ gap: "0.25rem", whiteSpace: "nowrap" }}>
                    <BsClock className="flex-shrink-0" style={{ verticalAlign: "middle" }} />
                    <span style={{ verticalAlign: "middle" }}>{tradingDaysLabel}</span>
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </ProductListing>
    <style jsx>{`
      .availability-tag {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 1;
      }
    `}</style>
    </>
  );
}
